import {useState, useEffect} from 'react';
import {apiRequest} from '../../config/api';
import {useAuth} from '../../contexts/PhpAuthContext';
import {useHospitalAuth} from '../../contexts/HospitalAuthContext';
import {toast} from '../ui/use-toast';
import {Button} from '../ui/button';
import {Input} from '../ui/input';
import {Label} from '../ui/label';
import {Card, CardContent, CardHeader, CardTitle} from '../ui/card';
import {Eye, EyeOff, RotateCcw} from 'lucide-react';
import {Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter} from '../ui/dialog';

export default function ChangePassword ({onClose}) {
  const {profile} = useAuth();
  const {hospitalUser} = useHospitalAuth();
  
  // Use hospitalUser if available (hospital login), otherwise use profile (regular login)
  const currentUser = hospitalUser || profile;
  const [newPassword, setNewPassword] = useState ('');
  const [confirmPassword, setConfirmPassword] = useState ('');
  const [message, setMessage] = useState ('');
  const [canReset, setCanReset] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [defaultPassword, setDefaultPassword] = useState('');

  useEffect(() => {
    // Check if user has permission to reset password
    // Backend now converts to boolean, but handle both boolean and numeric values for safety
    if (currentUser) {
      const canResetValue = currentUser.can_reset_password;
      // Accept true, 1, or "1" as enabled
      setCanReset(
        canResetValue === true || 
        canResetValue === 1 || 
        canResetValue === '1'
      );
    } else {
      setCanReset(false);
    }
  }, [currentUser]);

  const handleChangePassword = async e => {
    e.preventDefault ();
    setMessage ('');

    if (!canReset) {
      toast({
        title: 'Password Reset Disabled',
        description: 'Your administrator has disabled password reset for your account. Please contact your administrator.',
        variant: 'destructive'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage ('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setMessage ('Password must be at least 6 characters');
      return;
    }

    try {
      const data = await apiRequest('update_password.php', {
        method: 'POST',
        body: JSON.stringify({password: newPassword}),
      });

      if (data.success) {
        setMessage ('Password updated successfully!');
        setNewPassword ('');
        setConfirmPassword ('');
        toast({
          title: 'Success',
          description: 'Your password has been updated successfully.',
          variant: 'default'
        });
        if (onClose) {
          setTimeout(() => onClose(), 1000);
        }
      } else {
        setMessage (`Error: ${data.error || 'Failed to update password'}`);
        toast({
          title: 'Error',
          description: data.error || data.message || 'Failed to update password',
          variant: 'destructive'
        });
      }
    } catch (error) {
      setMessage (`Error: ${error.message || 'Failed to update password'}`);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update password',
        variant: 'destructive'
      });
    }
  };

  const handleResetToDefault = async () => {
    setResetting(true);
    setMessage('');

    try {
      const data = await apiRequest('reset_password_to_default.php', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (data.success) {
        setDefaultPassword(data.default_password || '');
        setShowResetDialog(true);
        toast({
          title: 'Password Reset Successfully',
          description: 'Your password has been reset to the default password.',
          variant: 'default'
        });
      } else {
        setMessage(`Error: ${data.error || 'Failed to reset password'}`);
        toast({
          title: 'Error',
          description: data.error || data.message || 'Failed to reset password',
          variant: 'destructive'
        });
      }
    } catch (error) {
      setMessage(`Error: ${error.message || 'Failed to reset password'}`);
      toast({
        title: 'Error',
        description: error.message || 'Failed to reset password',
        variant: 'destructive'
      });
    } finally {
      setResetting(false);
    }
  };

  if (!canReset) {
    return (
      <Card className="glass-effect border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Password Reset Disabled</CardTitle>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-red-400 mb-2">
            Your administrator has disabled password reset for your account.
          </p>
          <p className="text-gray-400 text-sm">
            Please contact your administrator to reset your password.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-effect border-gray-700">
      <CardHeader>
        <CardTitle className="text-white">Change Password</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-white">
              New Password
            </Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? "text" : "password"}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="bg-gray-800 border-gray-600 text-white pr-10"
                placeholder="Enter new password (min 6 characters)"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-white">
              Confirm Password
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="bg-gray-800 border-gray-600 text-white pr-10"
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          {message && (
            <p className={`text-sm ${message.includes('Error') ? 'text-red-400' : 'text-green-400'}`}>
              {message}
            </p>
          )}

          <div className="flex flex-col gap-3">
            <div className="flex gap-2">
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
              >
                Change Password
              </Button>
              {onClose && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </Button>
              )}
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-600"></span>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-gray-900 px-2 text-gray-400">Or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleResetToDefault}
              disabled={resetting}
              className="w-full border-yellow-500 text-yellow-400 hover:bg-yellow-500 hover:text-white"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {resetting ? 'Resetting...' : 'Reset to Default Password'}
            </Button>
          </div>
        </form>
      </CardContent>

      {/* Default Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent className="sm:max-w-[500px] bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Password Reset to Default</DialogTitle>
            <DialogDescription className="text-gray-300">
              Your password has been reset to the default password. Please save this password securely.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-white mb-2 block">Your Default Password:</Label>
            <div className="bg-gray-800 border border-gray-600 rounded-lg p-4">
              <p className="text-2xl font-mono font-bold text-yellow-400 text-center">
                {defaultPassword}
              </p>
            </div>
            <p className="text-xs text-gray-400 mt-3 text-center">
              {currentUser?.role === 'patient' 
                ? 'This is your phone number. Please change it to a secure password after logging in.'
                : 'Please change this password to a secure one after logging in.'}
            </p>
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setShowResetDialog(false);
                if (onClose) {
                  onClose();
                }
              }}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              I've Saved It
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
